/**
 * Reusable Schema.org JSON-LD builders. Every builder returns a plain JS
 * object that `<SeoHead jsonLd={…} />` serialises. Keep per-page content
 * entity-aware (reference `@id`s pointing back at /#organization etc.)
 */

const SITE_URL = "https://leadiqpro.io";
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

/* ─────────────────── Breadcrumb ─────────────────── */
export function breadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
    })),
  };
}

/* ─────────────────── FAQ ─────────────────── */
export function faqPage(questions) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((q) => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.a,
      },
    })),
  };
}

/* ─────────────────── Service / Offer ─────────────────── */
export function service({ name, description, path, priceMin, priceMax }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: `${SITE_URL}${path}`,
    provider: { "@id": ORG_ID },
    areaServed: { "@type": "AdministrativeArea", name: "Worldwide" },
    availableLanguage: ["English"],
    serviceType: "Lead enrichment · Sales intelligence",
    ...(priceMin !== undefined && priceMax !== undefined
      ? {
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "USD",
            lowPrice: priceMin,
            highPrice: priceMax,
          },
        }
      : {}),
  };
}

/* ─────────────────── Tool / SoftwareApplication (per feature) ─────────────────── */
export function softwareTool({ name, description, path, category = "BusinessApplication" }) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description,
    url: `${SITE_URL}${path}`,
    operatingSystem: "Web",
    applicationCategory: category,
    publisher: { "@id": ORG_ID },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    isAccessibleForFree: true,
    inLanguage: "en-US",
  };
}

/* ─────────────────── Collection + ItemList ─────────────────── */
export function itemList({ name, description, path, items }) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url: `${SITE_URL}${path}`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: items.map((it, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: it.name,
        description: it.description,
        url: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
      })),
    },
  };
}

/* ─────────────────── Pricing offer per tier ─────────────────── */
export function offerCatalog({ tiers, path = "/pricing" }) {
  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name: "LeadIQ Pro — plans and pricing",
    url: `${SITE_URL}${path}`,
    itemListElement: tiers.map((t) => ({
      "@type": "Offer",
      name: t.name,
      description: t.description,
      price: t.price,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}${path}`,
      itemOffered: {
        "@type": "Service",
        name: `LeadIQ Pro — ${t.name}`,
        description: t.description,
        provider: { "@id": ORG_ID },
      },
    })),
  };
}

/* ─────────────────── Simple WebPage wrapper ─────────────────── */
export function webPage({ name, description, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    description,
    url: `${SITE_URL}${path}`,
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORG_ID },
    inLanguage: "en-US",
  };
}
